import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { unsubscribeByToken, resubscribeByToken } from '../api/me';

/**
 * Public landing page for the unsubscribe link in reminder emails. Token-driven,
 * no login required. On load it calls the backend unsubscribe endpoint and offers
 * a token-driven re-subscribe. The button is disabled when no token is present.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const type = params.get('type') === 'digest' ? 'digest' : undefined;
  const [state, setState] = useState<'working' | 'done' | 'resubscribed'>('working');

  useEffect(() => {
    if (!token) {
      setState('done');
      return;
    }
    unsubscribeByToken(token, type).finally(() => setState('done'));
  }, [token, type]);

  async function onResubscribe() {
    await resubscribeByToken(token, type);
    setState('resubscribed');
  }

  return (
    <section data-testid="page-unsubscribe" className="mx-auto max-w-md text-center">
      {state === 'resubscribed' ? (
        <p className="mt-8">
          {type === 'digest'
            ? "You're subscribed to the daily results email again."
            : "You're subscribed to reminders again."}
        </p>
      ) : (
        <>
          <h1 className="mt-8 text-2xl font-bold">You&apos;re unsubscribed</h1>
          <p className="mt-2 text-stone-600">
            {type === 'digest'
              ? "You've unsubscribed from the daily results email."
              : "You won't get reminder emails from Scrabble Daily."}
          </p>
          <button
            className="mt-6 rounded-lg bg-emerald-700 px-4 py-2 font-semibold text-white disabled:opacity-50"
            disabled={!token}
            onClick={onResubscribe}
          >
            Re-subscribe
          </button>
        </>
      )}
      <p className="mt-6 text-sm">
        <Link to="/settings" className="text-emerald-700 underline">
          Manage in Settings
        </Link>
      </p>
    </section>
  );
}
